/**
 * Tabla de referencia renderizada en el servidor.
 *
 * Por que existe: las tablas del sitio las pintaba @mui/x-data-grid, que es
 * virtualizado — mide el contenedor en el navegador y no emite ninguna fila
 * durante el render del servidor. Medido sobre out/ despues de un build limpio:
 * `<tr>` = 0 y `<td>` = 0 en las 29 paginas publicadas, incluida /building, que
 * en su propio texto anuncia "440 rows in total". O sea que el dato compilado a
 * mano por jugadores —lo unico realmente original que tiene el sitio— no existia
 * en la respuesta HTML de ninguna pagina.
 *
 * Eso es la definicion de thin content de la politica de AdSense ("pages with
 * little to no content") y roza la de Publisher Policies ("sites should not claim
 * that they provide content or services that they do not have"): la pagina
 * prometia 440 filas y entregaba cero.
 *
 * Reglas que sigue este componente, y por que:
 *
 *  1. **Emite TODAS las filas en el HTML, sin paginar.** Paginar en el cliente
 *     sobre un HTML que trae todo significa servir una cosa al rastreador y otra
 *     al usuario, que es justo lo que la politica llama cloaking. El scroll vive
 *     en el contenedor, no en el numero de filas.
 *  2. **Nada se oculta.** Sin `<noscript>`, sin texto fuera de pantalla, sin una
 *     copia para el rastreador y otra para el usuario. Hay una sola tabla y es la
 *     que se ve.
 *  3. **El filtro y el orden son mejora progresiva.** El estado inicial es el
 *     dataset completo en su orden natural, asi que el servidor y el primer
 *     render del cliente coinciden y React no rehidrata distinto.
 *  4. **`<caption>` y `scope` en los encabezados.** Una tabla de 440 filas sin
 *     caption es ilegible con lector de pantalla, y el caption tambien le dice al
 *     rastreador de que es la tabla.
 *
 * Costo medido: el HTML de /building sube ~50 KB (~8 KB gzip) y a cambio la
 * pagina deja de cargar @mui/x-data-grid. Es ganancia neta de peso, no un
 * intercambio.
 */
import { useMemo, useState } from 'react'

import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import Typography from '@mui/material/Typography'
import { ClearAll } from '@mui/icons-material'

const normalizar = v => (v ?? '').toString().toLocaleLowerCase()

/**
 * @param {object[]} columnas  { clave, encabezado, numerica?, formato?, valor?, ancho? }
 * @param {object[]} filas     el dataset completo
 * @param {string}   idFila    campo o funcion que da una key estable por fila
 * @param {string}   titulo    va al <caption>; describe la tabla, no la pagina
 * @param {string[]} clavesFiltro  campos sobre los que busca el filtro
 */
const TablaReferencia = ({
  columnas,
  filas,
  idFila,
  titulo,
  nota,
  clavesFiltro = [],
  etiquetaFiltro = 'Data Filter',
  altoMaximo = 620
}) => {
  const [filtro, setFiltro] = useState('')
  const [orden, setOrden] = useState({ clave: null, dir: 'asc' })

  const clave = f => (typeof idFila === 'function' ? idFila(f) : f[idFila])

  const visibles = useMemo(() => {
    let out = filas

    const q = normalizar(filtro).trim()
    if (q) {
      const campos = clavesFiltro.length ? clavesFiltro : columnas.map(c => c.clave)
      out = out.filter(f => campos.some(k => normalizar(f[k]).includes(q)))
    }

    if (orden.clave) {
      const col = columnas.find(c => c.clave === orden.clave)
      const valor = f => (col?.valor ? col.valor(f) : f[orden.clave])
      out = [...out].sort((a, b) => {
        const va = valor(a)
        const vb = valor(b)

        const cmp =
          typeof va === 'number' && typeof vb === 'number' ? va - vb : normalizar(va).localeCompare(normalizar(vb))

        return orden.dir === 'asc' ? cmp : -cmp
      })
    }

    return out
  }, [filas, filtro, orden, columnas, clavesFiltro])

  const ordenarPor = c => {
    if (!c.ordenable) return
    setOrden(o => ({ clave: c.clave, dir: o.clave === c.clave && o.dir === 'asc' ? 'desc' : 'asc' }))
  }

  return (
    <Box>
      {clavesFiltro.length > 0 && (
        <Paper
          elevation={6}
          sx={{
            padding: '2px 4px',
            display: 'flex',
            alignItems: 'center',
            width: '250px',
            margin: '10px',
            border: '1px solid rgb(49 113 235 / 39%)'
          }}
        >
          <InputBase
            sx={{ marginLeft: theme => theme.spacing(1), flex: 1, width: '75px' }}
            placeholder={etiquetaFiltro}
            inputProps={{ 'aria-label': 'filter information' }}
            onChange={e => setFiltro(e.target.value)}
            value={filtro}
          />
          <Divider orientation='vertical' />
          <IconButton aria-label='clear' onClick={() => setFiltro('')}>
            <ClearAll />
          </IconButton>
        </Paper>
      )}

      {/*
        aria-live para que el lector de pantalla anuncie el resultado del filtro:
        sin esto el usuario escribe y no recibe ninguna señal de que la tabla
        cambio debajo.
      */}
      <Typography variant='caption' sx={{ px: '10px', color: 'text.secondary' }} aria-live='polite'>
        {filtro
          ? `${visibles.length.toLocaleString()} of ${filas.length.toLocaleString()} rows match "${filtro}"`
          : `${filas.length.toLocaleString()} rows`}
      </Typography>

      <TableContainer sx={{ maxHeight: altoMaximo, mt: 1 }}>
        <Table stickyHeader size='small' aria-label={titulo}>
          {/*
            El caption se muestra: describe la tabla para quien la lee y para el
            rastreador. Ocultarlo seria texto para maquina y no para persona.
          */}
          <caption style={{ captionSide: 'top', textAlign: 'left', padding: '8px 10px' }}>
            <Typography variant='body2' component='span' sx={{ color: 'text.secondary' }}>
              {titulo}
              {nota ? ` — ${nota}` : ''}
            </Typography>
          </caption>
          <TableHead>
            <TableRow>
              {columnas.map(c => (
                <TableCell
                  key={c.clave}
                  scope='col'
                  align={c.numerica ? 'right' : 'left'}
                  sx={{ whiteSpace: 'nowrap', width: c.ancho }}
                  sortDirection={orden.clave === c.clave ? orden.dir : false}
                >
                  {c.ordenable ? (
                    <TableSortLabel
                      active={orden.clave === c.clave}
                      direction={orden.clave === c.clave ? orden.dir : 'asc'}
                      onClick={() => ordenarPor(c)}
                    >
                      {c.encabezado}
                    </TableSortLabel>
                  ) : (
                    c.encabezado
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibles.map((f, i) => (
              <TableRow key={clave(f) ?? i} hover>
                {columnas.map((c, j) => {
                  const bruto = c.valor ? c.valor(f) : f[c.clave]
                  const texto = c.formato ? c.formato(bruto, f) : bruto

                  return (
                    <TableCell
                      key={c.clave}
                      component={j === 0 ? 'th' : 'td'}
                      scope={j === 0 ? 'row' : undefined}
                      align={c.numerica ? 'right' : 'left'}
                      sx={{
                        whiteSpace: 'nowrap',
                        fontWeight: j === 0 ? 600 : 400,
                        ...(c.numerica ? { fontVariantNumeric: 'tabular-nums' } : {})
                      }}
                      /*
                        El numero completo va en title: la celda muestra 61.5M
                        porque 61.500.000 no cabe en una tabla de 7 columnas, pero
                        el dato exacto tiene que quedar disponible. Es la cifra que
                        la gente viene a buscar.
                      */
                      title={c.numerica && typeof bruto === 'number' ? bruto.toLocaleString() : undefined}
                    >
                      {texto}
                    </TableCell>
                  )
                })}
              </TableRow>
            ))}
            {visibles.length === 0 && (
              <TableRow>
                <TableCell colSpan={columnas.length}>
                  <Typography variant='body2' align='center' sx={{ py: 2 }}>
                    {`There are no results for "${filtro}"`}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default TablaReferencia
